import { drawBitmap } from '../helpers/bitmap';
import { isRenderZoomUsable, quantizeRenderZoom } from '../helpers/zoom';
import { PageInfo, TilePart } from '../types';
import { isAbortError } from './errors';
import { PdfViewerClient, tileGrid } from './pdfViewerClient';

export interface PageTileLayersOptions {
    box: HTMLElement;
    client: PdfViewerClient;
    index: number;
    page: PageInfo;
    getZoom: () => number;
    layerClass: string;
    pendingClass: string;
}

export const LAYER_RZ_ATTRIBUTE = 'data-rz';

export class PageTileLayers {
    private controller: AbortController | null = null;

    private token = 0;

    constructor(private readonly options: PageTileLayersOptions) {}

    mount(): void {
        const {
            box, client, index, page, getZoom, layerClass, pendingClass,
        } = this.options;
        const renderZoom = quantizeRenderZoom(getZoom(), client.dpr);
        if (this.findLayer(renderZoom) || this.committedLayerFits()) {
            return;
        }
        this.token += 1;
        const { token } = this;
        const controller = this.controller ?? new AbortController();
        this.controller = controller;
        const hasCurrent = !!this.committedLayer();
        this.pendingLayers().forEach((layer) => PageTileLayers.dropLayer(layer));
        const layer = document.createElement('div');
        layer.className = hasCurrent ? `${layerClass} ${pendingClass}` : layerClass;
        layer.setAttribute(LAYER_RZ_ATTRIBUTE, String(renderZoom));
        layer.style.setProperty('--rz', String(renderZoom));
        box.appendChild(layer);

        const { columns, rows } = tileGrid(page, renderZoom, client.tileSize);
        let placed = 0;
        const decodes: Promise<void>[] = [];
        const isValid = (): boolean => token === this.token && layer.isConnected;
        const settled = (): Promise<unknown> => Promise.all(decodes);
        client.loadPageTiles({
            page: index,
            pageSize: page,
            renderZoom,
            signal: controller.signal,
            onTile: (tile) => {
                if (isValid()) {
                    decodes.push(this.placeTile(layer, tile).then((isPlaced) => {
                        placed += isPlaced ? 1 : 0;
                    }));
                }
            },
        }).then(settled).then(() => {
            if (isValid() && placed) {
                this.commit(layer);
            }
        }).catch(async (error) => {
            if (isAbortError(error)) {
                return;
            }
            await settled();
            if (isValid() && placed >= columns * rows) {
                this.commit(layer);
            }
        });
    }

    settle(): void {
        const { client, getZoom } = this.options;
        if (this.findLayer(quantizeRenderZoom(getZoom(), client.dpr)) || this.committedLayerFits()) {
            return;
        }
        this.controller?.abort();
        this.controller = new AbortController();
        this.mount();
    }

    stop(): void {
        this.token += 1;
        this.controller?.abort();
        this.controller = null;
        this.options.box.querySelectorAll(`[${LAYER_RZ_ATTRIBUTE}]`)
            .forEach((layer) => PageTileLayers.dropLayer(layer));
    }

    private findLayer(renderZoom: number): Element | null {
        return this.options.box.querySelector(`[${LAYER_RZ_ATTRIBUTE}="${renderZoom}"]`);
    }

    private committedLayerFits(): boolean {
        const { client, getZoom } = this.options;
        const layerZoom = Number(this.committedLayer()?.getAttribute(LAYER_RZ_ATTRIBUTE));
        return layerZoom > 0 && isRenderZoomUsable(layerZoom, getZoom(), client.dpr);
    }

    private pendingLayers(): Element[] {
        return Array.from(this.options.box.querySelectorAll(`.${this.options.pendingClass}`));
    }

    private committedLayer(): Element | null {
        return this.options.box.querySelector(`[${LAYER_RZ_ATTRIBUTE}]:not(.${this.options.pendingClass})`);
    }

    private async placeTile(layer: HTMLElement, { x, y, blob }: TilePart): Promise<boolean> {
        const { client } = this.options;
        try {
            const bitmap = await client.decodeBlob(blob);
            if (!layer.isConnected) {
                client.release(bitmap);
                return false;
            }
            const canvas = document.createElement('canvas');
            drawBitmap(canvas, bitmap);
            canvas.style.width = `${bitmap.width / client.dpr}px`;
            canvas.style.left = `${x * client.tileCssSize}px`;
            canvas.style.top = `${y * client.tileCssSize}px`;
            layer.appendChild(canvas);
            client.release(bitmap);
            return true;
        } catch {
            return false;
        }
    }

    private commit(layer: HTMLElement): void {
        if (!layer.isConnected) {
            return;
        }
        layer.classList.remove(this.options.pendingClass);
        this.options.box.querySelectorAll(`[${LAYER_RZ_ATTRIBUTE}]`).forEach((other) => {
            if (other !== layer) {
                PageTileLayers.dropLayer(other);
            }
        });
    }

    private static dropLayer(layer: Element): void {
        layer.remove();
    }
}
